import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import { useAuth } from 'provider/src/module/auth/auth-context.ts';
import { PushNotificationCoordinator } from '@client/lib/push-notifications/service/push-notification-coordinator';
import { TokenManager } from '@client/lib/push-notifications/shared/token-manager';
import { notificationNavigator } from '@client/lib/push-notifications/shared/notification-navigator';

export interface PushNotificationService {
  currentToken: string | undefined;
  enable(): Promise<void> | undefined;
}

export function usePushNotificationServiceProvider(): PushNotificationService {
  const [currentToken, setCurrentToken] = useState<string>();
  const [permissionRequested, setPermissionRequested] = useState(false);

  const notificationCenter = useNotificationCenter();
  const authContext = useAuth();
  const navigate = useNavigate();

  // Create coordinator instance
  const coordinator = useMemo(() => new PushNotificationCoordinator(), []);

  // Set navigate function for notification navigator
  useEffect(() => {
    notificationNavigator.setNavigate(navigate);
  }, [navigate]);

  // Initialize coordinator on mount
  useEffect(() => {
    coordinator.initialize().catch((error) => {
      console.error('Failed to initialize push notification coordinator:', error);
    });

    // Setup token refresh listener
    coordinator.onTokenRefresh((token) => {
      setCurrentToken(token);
      TokenManager.registerToken(token).catch((error) => {
        console.error('Failed to register token on refresh:', error);
      });
    });

    // Setup message listener
    coordinator.onMessage((message) => {
      console.log('Push notification received:', message);
      // Handle incoming messages here
    });

    // Setup action listener for notification taps
    coordinator.onAction((payload) => {
      console.log('Push notification tapped:', payload);
      notificationNavigator.handleNotificationTap(payload);
    });

    return () => {
      coordinator.cleanup();
    };
  }, [coordinator]);

  // Request permission and retrieve token
  const requestPermissionAndGetToken = useCallback(async (showErrorAlert = true) => {
    const granted = await coordinator.requestPermission();
    setPermissionRequested(true);

    if (!granted) {
      if (showErrorAlert) {
        notificationCenter.showAlert({
          title: 'Notification permission denied',
          message: 'You will not receive notifications.',
          status: 'error',
        });
      }
      return false;
    }

    const token = await coordinator.getToken();
    if (token) {
      setCurrentToken(token);
      await TokenManager.registerToken(token);
      return true;
    }

    return false;
  }, [coordinator, notificationCenter]);

  const enable = useCallback(() => {
    if (import.meta.env.PROD || import.meta.env.VITE_FORCE_ENABLE_FIREBASE === 'true') {
      return requestPermissionAndGetToken()
        .then((success) => {
          if (success) {
            notificationCenter.showAlert({
              title: 'Push notification enabled',
              message: 'You will now receive push notifications.',
              status: 'success',
            });
          }
        })
        .catch((error) => {
          console.error('Failed to enable push notifications:', error);
          notificationCenter.showAlert({
            title: 'Failed to enable notifications',
            message: 'An error occurred while enabling push notifications.',
            status: 'error',
          });
        });
    }
  }, [notificationCenter, requestPermissionAndGetToken]);

  // Auto-retrieve token on auth (only once)
  useEffect(() => {
    const isProduction = import.meta.env.PROD || import.meta.env.VITE_FORCE_ENABLE_FIREBASE === 'true';

    if (isProduction && authContext.auth != null && !permissionRequested) {
      // Don't show error alert for auto-request (silent failure)
      requestPermissionAndGetToken(false).catch(() => void 0);
    }
  }, [authContext.auth, requestPermissionAndGetToken, permissionRequested]);

  return {
    currentToken,
    enable,
  };
}
